import React, { useCallback, useState } from 'react';

import { Form, Formik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import PasswordFieldTransparent from '@/components/shared/form/PasswordFieldTransparent';
import { BtnOneLineBlue } from '@/components/buttons/BtnOneLineBlue';
import PromptMessage from '@/components/UI/PromptMessage';
import ReactCountryFlag from 'react-country-flag';
import { CANADA_PROVINCES, FAST_REG_COUNTRIES, FAST_REG_EU_COUNTRIES } from '@/constants/locales';
import { selectIpData, selectLocale } from '@/store/selectors/commonSelectors';
import { selectUserBtag } from '@/store/selectors/combined.selectors';
import { sha256Hash } from '@/shared/crypto-utils';
import { logException } from '@/utils/logger';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { loginSuccess } from '@/store/actions/auth';
import { triggerNetreferSignup } from '@/shared/netrefer';
import SearchDropdownField from '@/components/form/search-dropdown/SearchDropdownField';
import { selectRegistrationOpenedFrom } from '@/store/ui/selectors';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import {
  HomeHeroAuthenticationStep,
  useHomeHeroAuthenticationStepActions,
} from '@/store/authentication/AuthenticationStore';
import { useRewardsWheel } from '@/hooks/useRewardsWheel';
import TextInputFieldTransparent from '@/components/shared/form/TextInputFieldTransparent';
import StorageManager, { ReturningUserLoginMethod } from '@/shared/storageManager';
import { sendEventToAppWrapper } from '@/utils/app-wrapper-utils';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import {
  BffErrorCode,
  BffRegisterFastRequest,
  BffRegisterResponse,
} from '@lucky7ventures/bff-types';
import BffApiService from '@/shared/bffApiService';
import { BffApiError } from '@/models/bff-api-error';
import { generateBffErrorMessageWithCode } from '@/utils/utils';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import PhoneNumberField from '@/components/form/phone-number/PhoneNumberField';
import { MobileInputModel } from '@/models/mobile-input.model';
import { useMobileNumberVerificationRequired } from '@/features/data-loader/hooks/useMobileNumberVerificationRequired';

type FormValues = {
  fullName: string;
  email: string;
  country: string;
  province: string;
  password: string;
  mobile?: MobileInputModel;
};

const provinces = CANADA_PROVINCES.map(province => ({
  value: province.code,
  label: province.name,
}));

const FastRegistrationForm = () => {
  const [error, setError] = useState<string | undefined>();
  const { mobileNumberVerificationRequired } = useMobileNumberVerificationRequired();
  const { request, loading, success } = useBffApiRequest<BffRegisterResponse>();
  const intl = useIntl();
  const dispatch = useDispatch();
  const locale = useSelector(selectLocale);
  const ipData = useSelector(selectIpData);
  const btag = useSelector(selectUserBtag);
  const registrationOpenedFrom = useSelector(selectRegistrationOpenedFrom);
  const navigate = useAbsoluteNavigate();
  const { pagePath: rewardsWheelPagePath } = useRewardsWheel();
  const isRowOrEuLocale = locale === 'row' || locale === 'eu';
  const { setAuthenticationCredentials, setHomeHeroAuthenticationStep, setPostRegisterData } =
    useHomeHeroAuthenticationStepActions();
  const { openModal } = useModal();
  const fastRegCountries = locale === 'eu' ? FAST_REG_EU_COUNTRIES : FAST_REG_COUNTRIES;
  const countries = fastRegCountries.map(country => ({
    value: country.code,
    label: country.name,
    endIcon: <ReactCountryFlag countryCode={country.code.toLocaleUpperCase()} />,
  }));

  const handleLogin = () => {
    triggerGTag(GTagEvents.home_auth_login_click);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    setHomeHeroAuthenticationStep(HomeHeroAuthenticationStep.Login);
  };

  const isCanadaLocale = locale === 'ca' || locale === 'ca-fr';

  const defineInitialCountry = useCallback(() => {
    if (isCanadaLocale) return 'ca';
    if (isRowOrEuLocale) {
      // Check if the country from IP data is in the array of our supported countries
      const activeCountry =
        countries.find(country => country.value === ipData.country.toLowerCase())?.value ?? '';
      if (ipData && ipData.country && activeCountry) {
        return activeCountry;
      } else {
        return '';
      }
    }
    return locale;
  }, [ipData, locale]);

  const initialFormValues: FormValues = {
    fullName: '',
    email: '',
    country: defineInitialCountry(),
    mobile: {
      number: '',
      country: defineInitialCountry(),
      prefix: '',
      wholeNumber: '',
    },
    province: '',
    password: '',
  };

  const formSchema = Yup.object().shape({
    fullName: Yup.string()
      .matches(
        /^[\p{L}\p{M}'-]+(\s[\p{L}\p{M}'-]+)+$/u,
        intl.formatMessage({ id: 'fastReg.name.error.invalid' }),
      )
      .trim()
      .required(),
    email: Yup.string()
      .required()
      .email(intl.formatMessage({ id: 'fastReg.email.error.invalid' })),
    country: Yup.string().required(),
    province: Yup.object().when('country', country => {
      if (country === 'ca' || isCanadaLocale) {
        return Yup.string().required();
      }
      return Yup.string().notRequired();
    }),
    mobile: mobileNumberVerificationRequired
      ? Yup.object().shape({
          wholeNumber: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
          number: Yup.string().min(5, intl.formatMessage({ id: 'inputs.mobile.invalid' })),
        })
      : Yup.object().shape({}),
    password: Yup.string()
      .test(
        'no-whitespace',
        intl.formatMessage({ id: 'fastReg.password.whitespaces.invalid' }),
        value => !(value && /\s/.test(value)),
      )
      .matches(/^\S{6,}$/, intl.formatMessage({ id: 'fastReg.password.invalid' }))
      .required(),
  });

  const handleSubmit = (values: FormValues) => {
    // push the hashed email to the dataLayer
    sha256Hash(values.email)
      .then(hashedEmail => {
        window.dataLayer.push({
          email: hashedEmail,
        });
      })
      .catch(e => logException(e));

    const handleError = (error: BffApiError) => {
      if (
        error.code === BffErrorCode.GIG_USERNAME_ALREADY_EXISTS ||
        error.code === BffErrorCode.GIG_EMAIL_ALREADY_EXISTS
      ) {
        openModal(MODALS.BASIC, {
          title: intl.formatMessage({ id: 'fastReg.existingUser.title' }),
          body: intl.formatMessage({ id: 'fastReg.existingUser.body' }),
          btnText: intl.formatMessage({ id: 'login.login' }),
          onBtnClick: () => {
            openModal(MODALS.LOGIN);
            triggerGTag(GTagEvents.fast_reg_existing_acc_login_click);
          },
          onClose: () => triggerGTag(GTagEvents.fast_reg_existing_acc_close),
        });
        triggerGTag(GTagEvents.fast_reg_existing_acc_show, {
          error: error.code,
        });
      } else if (error.code === BffErrorCode.GIG_ACCOUNT_BLOCKED) {
        openModal(MODALS.ACCOUNT_BLOCKED);
        triggerGTag(GTagEvents.fast_reg_account_blocked, { error: error.code });
      } else {
        const errorMessage = generateBffErrorMessageWithCode(
          intl.formatMessage({ id: 'failedSignup.SomethingWentWrongMessage' }),
          error.code,
        );
        setError(errorMessage);
        triggerGTag(GTagEvents.fast_reg_failed, { error: error.code });
        logException(error);
      }
    };

    const handleSuccess = (
      responseData: BffRegisterResponse,
      requestData: BffRegisterFastRequest,
    ) => {
      StorageManager.setIsReturningUser(ReturningUserLoginMethod.Email);
      if (responseData.isTwoFactorAuthentication) {
        triggerGTag(GTagEvents.register_2fa);
        setAuthenticationCredentials(requestData.email, requestData.password);
        setPostRegisterData(responseData.userId, btag);
        setHomeHeroAuthenticationStep(HomeHeroAuthenticationStep.TwoFactorAuthentication);
      } else {
        dispatch(loginSuccess(responseData.token!, { isRegistration: true }));
        triggerGTag(GTagEvents.fast_reg_success, { userId: responseData.userId });
        sendEventToAppWrapper(GTagEvents.fast_reg_success, {
          userId: responseData.userId,
        });
        triggerNetreferSignup(btag, responseData.userId, locale);

        // do specific redirects for some cases if registration was started on specific place
        if (registrationOpenedFrom === OpenedFrom.RewardsWheel) {
          navigate(`/${rewardsWheelPagePath}`);
        }
      }
    };

    let countryCode = '';
    if (isRowOrEuLocale) {
      countryCode = values.country;
    } else {
      if (isCanadaLocale) {
        // We send 'ca' for both /ca and /ca-fr locales
        countryCode = 'ca';
      } else {
        countryCode = locale;
      }
    }

    const payload = {
      email: values.email,
      password: values.password,
      firstName: values.fullName.split(' ')[0],
      lastName: values.fullName.split(' ').slice(1).join(' '),
      countryCode,
      ...((isCanadaLocale || values.country === 'ca') && {
        provinceCode: values.province,
      }),
      mobile: mobileNumberVerificationRequired ? values.mobile?.number : undefined,
      mobilePrefix: mobileNumberVerificationRequired ? values.mobile?.prefix : undefined,
      btag,
    };

    request({
      apiMethod: BffApiService.registerFast,
      payload,
      successCallback: data => {
        handleSuccess(data, payload);
      },
      errorCallback: handleError,
    });
  };

  return (
    <>
      {error && (
        <div className="[&_p]:!text-base">
          <PromptMessage type="error" message={error} />
        </div>
      )}
      <Formik
        initialValues={initialFormValues}
        validationSchema={formSchema}
        validateOnMount
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ isValid, values }) => {
          return (
            <Form
              id="e2e_fastReg"
              autoComplete="off"
              className="flex flex-col gap-4 [&>button:last-of-type]:!mx-0 [&>button:last-of-type]:min-h-[50px]"
            >
              <TextInputFieldTransparent
                type="text"
                name="fullName"
                label={intl.formatMessage({ id: 'inputs.fullName' })}
                onFocus={() => triggerGTag(GTagEvents.fast_reg_name_click)}
                onValidationError={error => triggerGTag(GTagEvents.fast_reg_name_error, { error })}
                className="capitalize"
                aria-label="Full name input field - registration"
              />
              <TextInputFieldTransparent
                data-cy="registration-form-email"
                type="email"
                name="email"
                label={intl.formatMessage({
                  id: 'inputs.email',
                })}
                onFocus={() => triggerGTag(GTagEvents.fast_reg_email_click)}
                onValidationError={error => triggerGTag(GTagEvents.fast_reg_email_error, { error })}
                autoComplete="new-email"
                aria-label="Email input field - registration"
              />
              <PasswordFieldTransparent
                label={intl.formatMessage({
                  id: 'inputs.password',
                })}
                name="password"
                onFocus={() => triggerGTag(GTagEvents.fast_reg_password_click)}
                onValidationError={error =>
                  triggerGTag(GTagEvents.fast_reg_password_error, { error })
                }
                autoComplete="new-password"
                aria-label="Password input field - registration"
              />
              {isRowOrEuLocale && (
                <SearchDropdownField
                  labelClassName="text-white"
                  inputClassName="text-white"
                  name="country"
                  label={intl.formatMessage({
                    id: 'inputs.country',
                  })}
                  options={countries}
                  onFocus={() => triggerGTag(GTagEvents.fast_reg_country_click)}
                  onValidationError={error =>
                    triggerGTag(GTagEvents.fast_reg_country_error, { error })
                  }
                />
              )}
              {(isCanadaLocale || values.country === 'ca') && (
                <SearchDropdownField
                  aria-label="Country dropdown - registration"
                  labelClassName="text-white"
                  inputClassName="text-white"
                  name="province"
                  label={intl.formatMessage({
                    id: 'inputs.province',
                  })}
                  options={provinces}
                  onFocus={() => triggerGTag(GTagEvents.fast_reg_province_click)}
                  onValidationError={error =>
                    triggerGTag(GTagEvents.fast_reg_province_error, { error })
                  }
                />
              )}
              {mobileNumberVerificationRequired && (
                <PhoneNumberField
                  inputStyle={{
                    height: '56px',
                    border: '1px',
                    backgroundColor: 'rgb(255 255 255 / 0.2)',
                    width: '100%',
                    borderRadius: 0,
                    paddingBottom: '1rem',
                    paddingLeft: '3.75rem',
                    paddingRight: '0.25rem',
                    paddingTop: '1.25rem',
                    fontSize: '15px',
                    lineHeight: 1,
                  }}
                  label={intl.formatMessage({ id: 'inputs.number' })}
                  country={values.country}
                  name="mobile"
                  onFocus={() => triggerGTag(GTagEvents.fast_reg_phone_click)}
                  onValidationError={error =>
                    triggerGTag(GTagEvents.fast_reg_phone_error, { error })
                  }
                  className="border-[1px] border-white/30 text-white focus-within:border-blue-light
                [&_input]:placeholder:!text-white/60 mb-3"
                />
              )}
              <BtnOneLineBlue
                type="submit"
                isLoading={loading}
                disabled={!isValid}
                text={
                  !loading && success
                    ? intl.formatMessage({ id: 'fastReg.success' })
                    : intl.formatMessage({ id: 'header.register' })
                }
                onClickHandler={() => triggerGTag(GTagEvents.fast_reg_cta_click)}
              />
            </Form>
          );
        }}
      </Formik>
      <div className="text-base text-white mt-3">
        <FormattedMessage
          id="fastReg.login"
          values={{
            a: str => (
              <button
                type="button"
                aria-label="Switch to login"
                aria-roledescription="Button used to switch to login form"
                onKeyDown={e => {
                  if (e.code !== 'Tab') e.preventDefault();
                  handleLogin && handleLogin();
                }}
                data-cy="registration-form-switch-login-button"
                onClick={handleLogin}
                className="text-blue-lighter font-bold underline cursor-pointer focus:border-[1px]"
              >
                {str}
              </button>
            ),
          }}
        />
      </div>
      <p className="mt-3 block mb-0 !text-[12px] italic text-white">
        <FormattedMessage
          id="fastReg.consent.text"
          values={{
            a: str => (
              <button
                type="button"
                aria-roledescription="Button used to open Terms and conditions"
                aria-label="Open T&C"
                onKeyDown={e => {
                  if (e.code !== 'Tab') e.preventDefault();
                  handleLogin && handleLogin();
                }}
                onClick={() => navigate('/terms-and-conditions')}
                className="text-blue-lighter font-bold underline cursor-pointer focus:border-[1px]"
              >
                {str}
              </button>
            ),
          }}
        />
      </p>
    </>
  );
};

export default FastRegistrationForm;
